/**
 * Concept Drawings — service layer for the Concept Drawing tool (a PDF-backed "sketch" board
 * in a presentation's Sketches section that can be marked up + measured live in client
 * meetings). Backed by api/concept-drawings.js (Supabase `concept_drawings` + the public
 * `concept-drawings` bucket). See docs/CONCEPT_DRAWING.md.
 *
 * A sketch is "a live drawing" purely because a row exists here for its Airtable id — there is
 * no separate flag anywhere else. `getConceptDrawingsForSketches` is how SketchWall checks that
 * in bulk for the whole board.
 */

// The journey's PIN proof travels with every client-facing call: as of 2026-09-12 these routes
// require it, because a PIN is what ties a visit to a named person. The proof is minted when the
// client enters their PIN and cached for 30 days, so this is already present by the time any of
// this renders.
import { getCachedPinProof } from './journey.js'

/** Query-string fragment carrying the client's PIN proof for the public read paths. */
function proofQs(journeyToken) {
  return '&pinProof=' + encodeURIComponent(getCachedPinProof(journeyToken) || '')
}

async function authHeaders() {
  const token = await window.__clerkGetToken()
  return { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + token }
}

async function call(method, body, qs) {
  const res = await fetch('/api/concept-drawings' + (qs ? '?' + qs : ''), {
    method,
    headers: await authHeaders(),
    body: body ? JSON.stringify(body) : undefined,
  })
  const data = await res.json().catch(() => ({}))
  if (!res.ok) throw new Error(data.error || ('Concept drawing request failed (' + res.status + ')'))
  return data
}

async function putSigned(signedUrl, body, contentType) {
  const res = await fetch(signedUrl, {
    method: 'PUT',
    headers: { 'Content-Type': contentType, 'x-upsert': 'true' },
    body,
  })
  if (!res.ok) throw new Error('Upload failed (' + res.status + ')')
}

/** Batch existence check for a board of sketches (presenter/team). -> { [sketchId]: drawing|null } */
export async function getConceptDrawingsForSketches(sketchIds) {
  if (!sketchIds || !sketchIds.length) return {}
  const { drawings } = await call('GET', null, 'sketchIds=' + encodeURIComponent(sketchIds.join(',')))
  return drawings
}

/** Same check for the client-facing wall — no Clerk token, existence only. -> { [sketchId]: boolean } */
export async function getConceptDrawingsExistForClient(journeyToken, sketchIds) {
  if (!sketchIds || !sketchIds.length) return {}
  const res = await fetch('/api/concept-drawings?token=' + encodeURIComponent(journeyToken) + '&sketchIds=' + encodeURIComponent(sketchIds.join(',')) + proofQs(journeyToken))
  const data = await res.json().catch(() => ({}))
  if (!res.ok) throw new Error(data.error || ('Concept drawing request failed (' + res.status + ')'))
  return data.exists || {}
}

/** Full record (incl. markup) for the presenter/team-side tool. */
export async function getConceptDrawing(sketchId) {
  const { drawing } = await call('GET', null, 'sketchId=' + encodeURIComponent(sketchId))
  return drawing
}

/**
 * Public/client read — no Clerk token, scoped by the Client Hub's own access token.
 * Markup is omitted server-side unless the presenter has toggled it visible.
 */
export async function getConceptDrawingForClient(journeyToken, sketchId) {
  const res = await fetch('/api/concept-drawings?token=' + encodeURIComponent(journeyToken) + '&sketchId=' + encodeURIComponent(sketchId) + proofQs(journeyToken))
  const data = await res.json().catch(() => ({}))
  if (!res.ok) throw new Error(data.error || ('Concept drawing request failed (' + res.status + ')'))
  return data.drawing
}

/**
 * Upload (or replace) the PDF + rendered thumbnail for a sketch's drawing. Replacing an
 * existing drawing resets its scale + markup server-side (a new document invalidates both).
 * Returns the drawing id.
 */
export async function uploadConceptDrawing(sketchId, projectId, pdfBlob, thumbBlob) {
  const { id, pdfUpload, thumbUpload } = await call('POST', { action: 'uploadUrl', sketchId, projectId })
  await putSigned(pdfUpload.signedUrl, pdfBlob, 'application/pdf')
  await putSigned(thumbUpload.signedUrl, thumbBlob, 'image/png')
  return id
}

export async function setConceptDrawingScale(id, scalePixPerFoot) {
  const { drawing } = await call('POST', { action: 'setScale', id, scalePixPerFoot })
  return drawing
}

export async function saveConceptDrawingMarkup(id, markup) {
  const { drawing } = await call('POST', { action: 'saveMarkup', id, markup })
  return drawing
}

export async function setConceptDrawingMarkupVisible(id, visible) {
  const { drawing } = await call('POST', { action: 'setMarkupVisible', id, visible })
  return drawing
}

export function deleteConceptDrawing(id) {
  return call('POST', { action: 'delete', id })
}
